import { zodResolver } from '@hookform/resolvers/zod';
import clsx from 'clsx';
import { highlight, languages } from 'prismjs';
import React, { useEffect,useState } from 'react';
import { Controller,useForm } from 'react-hook-form';
import Editor from 'react-simple-code-editor';
import { z } from 'zod';
import 'prismjs/components/prism-json';
import 'prismjs/themes/prism.css';

import { Button } from '@/components/button/button';
import { ExampleTypeEnum, SourceConfigJson } from '@/types/evals';

import styles from './JsonConfigForm.module.scss';
import sharedStyles from './modalSourceCreate.module.scss';
import { jsonConfigSchema } from './schemas';

const formSchema = z.object( {
  config: jsonConfigSchema
} );

type FormData = z.infer<typeof formSchema>;

type JsonConfigFormProps = {
  initialData?: {
    example_type: ExampleTypeEnum;
    config: SourceConfigJson;
  };
  source_name: string;
  source_description?: string;
  source_type: 'json';
  onSubmit: ( data: FormData & { example_type: ExampleTypeEnum } ) => void;
  onCancel: () => void;
  onBack: () => void;
  isPending?: boolean;
  isEditMode: boolean;
};

export const JsonConfigForm: React.FC<JsonConfigFormProps> = ( {
  initialData,
  onSubmit: onSubmitProp,
  onCancel,
  onBack,
  isPending
} ) => {
  const [ jsonInput, setJsonInput ] = useState( '' );
  const [ jsonError, setJsonError ] = useState<string>();

  useEffect( () => {
    // Initialize with existing data if in edit mode
    if ( initialData?.config ) {
      setJsonInput( JSON.stringify( initialData.config, null, 2 ) );
    }
  }, [ initialData ] );

  const {
    control,
    handleSubmit,
    setValue,
    trigger,
    formState: { errors }
  } = useForm<FormData>( {
    resolver: zodResolver( formSchema ),
    defaultValues: initialData || {
      config: { examples: [] }
    }
  } );

  const handleJsonInputChange = ( value: string ) => {
    setJsonInput( value );
    setJsonError( undefined );

    if ( !value.trim() ) {
      setValue( 'config', { examples: [] } );
      return;
    }

    try {
      const parsed = JSON.parse( value );
      console.log( 'Parsed JSON:', parsed );
      setValue( 'config', parsed );
      // Trigger validation asynchronously to update error state
      trigger( 'config' ).then( ( isValid ) => {
        console.log( 'Config validation result:', isValid, 'Errors:', errors );
      } );
    } catch( e ) {
      setJsonError( e instanceof Error ? e.message : 'Invalid JSON format' );
      setValue( 'config', { examples: [] } );
    }
  };

  const detectExampleType = ( examples: Array<{ type: string }> ): ExampleTypeEnum | undefined => {
    if ( examples.length === 0 ) return undefined;
    return examples[ 0 ].type as ExampleTypeEnum;
  };

  const onSubmit = ( data: FormData ) => {
    const exampleType = detectExampleType( data.config.examples );
    if ( !exampleType ) {
      setJsonError( 'Cannot determine example type from examples' );
      return;
    }
    onSubmitProp( { ...data, example_type: exampleType } );
  };

  return (
    <form id="source-config-form" onSubmit={ handleSubmit( onSubmit ) } className={ sharedStyles.form_wrapper }>
      <section className={ sharedStyles.layout__source_type }>
        <h2>Create Source - JSON</h2>

        <div className={ clsx( 'layout__col', 'pad' ) }>
          <label
            className={ clsx(
              'auth_flow_label',
              ( errors.config || jsonError ) && 'required'
            ) }
            htmlFor="config.examples"
          >
            Examples JSON <span className={ sharedStyles.required_asterisk }>*</span>
          </label>
          <p className={ sharedStyles.field_description }>
            Paste your examples as JSON. Format: {'{ "examples": [...] }'}
          </p>
          <Controller
            name="config"
            control={ control }
            render={ () => (
              <div
                className={ clsx(
                  styles.json_editor_wrapper,
                  ( errors.config || jsonError ) && sharedStyles.input_error
                ) }
              >
                <Editor
                  value={ jsonInput }
                  onValueChange={ handleJsonInputChange }
                  highlight={ ( code ) => highlight( code, languages.json, 'json' ) }
                  padding={ 10 }
                  disabled={ isPending }
                  placeholder={ jsonPlaceholder }
                  style={ {
                    fontFamily: '"Fira code", "Fira Mono", monospace',
                    fontSize: 13,
                    minHeight: '400px',
                    maxHeight: '600px',
                    overflow: 'auto'
                  } }
                  textareaId="config.examples"
                  data-testid="source-config-json-examples"
                />
              </div>
            ) }
          />
          {jsonError && (
            <div className={ sharedStyles.error_message } data-testid="json-parse-error">
              {jsonError}
            </div>
          )}
          {errors.config && (
            <div className={ sharedStyles.error_message } data-testid="config-validation-error">
              {errors.config.message ||
               errors.config.examples?.message ||
               'Invalid configuration format. Check console for details.'}
            </div>
          )}
        </div>
      </section>

      <div className={ sharedStyles.form_actions }>
        <div className={ clsx( 'layout__row', 'pad', 'flex_right' ) }>
          <p className={ sharedStyles.validation_note }>
            Note: JSON sources will be validated when you click Create
          </p>
        </div>
      </div>

      <div className={ sharedStyles.form_actions }>
        <div className={ clsx( 'layout__row', 'pad', 'flex_right' ) }>
          <Button
            variant="secondary"
            size="sm"
            onClick={ onCancel }
            disabled={ isPending }
            data-testid="source-cancel-button"
          >
            Cancel
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={ onBack }
            disabled={ isPending }
            data-testid="source-back-button"
          >
            Back
          </Button>
          <Button
            variant="primary"
            size="sm"
            type="submit"
            loading={ isPending }
            disabled={ isPending || !!jsonError }
            data-testid="source-create-button"
          >
            Create
          </Button>
        </div>
      </div>
    </form>
  );
};

const jsonPlaceholder = `{
  "examples": [
    {
      "type": "message",
      "data": [
        {
          "user": "user",
          "message": "Hello"
        },
        {
          "user": "assistant",
          "message": "Hi there!"
        }
      ]
    }
  ]
}`;